<template>
  <div class="px-6 py-6 space-y-6 min-h-screen select-none text-left transition-colors duration-200 font-sans"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-zinc-50 text-zinc-600'">

    <div class="flex justify-between items-center border-b pb-5 transition-colors duration-200"
      :class="isDark ? 'border-zinc-900' : 'border-zinc-200'">
      <div class="space-y-0.5">
        <h1 class="text-xl font-semibold tracking-tight transition-colors duration-200" :class="isDark ? 'text-white' : 'text-zinc-900'">
          {{ isEditMode ? 'Modifier la prestation' : 'Nouvelle prestation' }}
        </h1>
        <p class="text-xs font-medium transition-colors duration-200" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
          {{ isEditMode ? 'Mise à jour des paramètres du service.' : 'Configuration d\'un nouveau service pour votre catalogue.' }}
        </p>
      </div>
      <button @click="router.push('/services')" 
        class="px-4 py-2 rounded-xl text-xs font-semibold uppercase tracking-wider transition-colors cursor-pointer border shadow-sm flex items-center gap-2"
        :class="isDark ? 'bg-zinc-900 hover:bg-zinc-800 border-zinc-800 text-zinc-300' : 'bg-white hover:bg-zinc-100 border-zinc-200 text-zinc-600'">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"/></svg>
        Retour
      </button>
    </div>

    <div class="max-w-2xl space-y-6">
      <div class="border rounded-2xl p-6 space-y-4 transition-colors"
        :class="isDark ? 'bg-zinc-900/20 border-zinc-900' : 'bg-white border-zinc-200 shadow-sm'">
        
        <div class="grid gap-4">
          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Nom du service *</label>
            <input v-model="form.nom" type="text" placeholder="Ex: Coupe & Styling" 
              class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium focus:outline-none transition-all"
              :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 focus:border-zinc-300 shadow-sm'" />
          </div>

          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Catégorie *</label>
            <select v-model="form.categorie" 
              class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium focus:outline-none appearance-none cursor-pointer transition-all"
              :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 focus:border-zinc-300 shadow-sm'">
              <option value="">Sélectionner une catégorie</option>
              <option v-for="cat in categories" :key="cat._id" :value="cat._id">{{ cat.nom }}</option>
            </select>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="space-y-1">
              <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Prix (€) *</label>
              <input v-model.number="form.prix" type="number" min="0" 
                class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium focus:outline-none transition-all"
                :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 focus:border-zinc-300 shadow-sm'" />
            </div>
            <div class="space-y-1">
              <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Durée (min) *</label>
              <input v-model.number="form.duree" type="number" min="15" step="15" 
                class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium focus:outline-none transition-all"
                :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 focus:border-zinc-300 shadow-sm'" />
            </div>
          </div>

          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Description</label>
            <textarea v-model="form.description" rows="3" 
              class="w-full p-4 rounded-xl border text-xs font-medium focus:outline-none resize-none transition-all"
              :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white placeholder-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 placeholder-zinc-400 shadow-sm'"></textarea>
          </div>

          <div class="flex items-center gap-3 pt-2">
            <input v-model="form.disponible_en_ligne" type="checkbox" 
              class="h-4 w-4 rounded border cursor-pointer transition-all"
              :class="isDark ? 'accent-white border-zinc-700 bg-zinc-900' : 'accent-zinc-900 border-zinc-300 bg-white'" />
            <label class="text-xs font-medium transition-colors" :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
              Disponible pour réservation en ligne
            </label>
          </div>
        </div>
      </div>

      <div class="flex gap-3">
        <button @click="router.push('/services')" 
          class="flex-1 px-4 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-colors cursor-pointer border shadow-sm"
          :class="isDark ? 'bg-zinc-900 hover:bg-zinc-800 border-zinc-800 text-zinc-400' : 'bg-white hover:bg-zinc-100 border-zinc-200 text-zinc-600'">
          Annuler
        </button>
        <button @click="handleSubmit" :disabled="isSubmitting" 
          class="flex-1 px-5 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all duration-300 disabled:opacity-50 cursor-pointer shadow-sm"
          :class="isDark ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-900 hover:bg-zinc-800 text-white'">
          {{ isSubmitting ? (isEditMode ? 'Mise à jour...' : 'Création...') : (isEditMode ? 'Modifier le service' : 'Créer le service') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { toast } from 'vue3-toastify'
import api from '@/api'
import { useTheme } from '@/composables/useTheme';

// Variable globale injectée ou reçue par props pour basculer de thème
const { isDark } = useTheme();
const router = useRouter()
const route = useRoute()
const isSubmitting = ref(false)
const categories = ref([])
const isEditMode = ref(false)
const editId = ref(null)

const form = reactive({
  nom: '',
  description: '',
  prix: 0,
  duree: 30,
  categorie: '',
  disponible_en_ligne: true
})

onMounted(async () => {
  try {
    // Load categories
    const response = await api.services.getAllCategories()
    categories.value = response.data.categories || response.data || []

    // Check if we're in edit mode
    if (route.query.edit) {
      isEditMode.value = true
      editId.value = route.query.edit
      await loadServiceData(editId.value)
    }
  } catch (error) {
    console.error('Erreur lors du chargement:', error)
    toast.error('Erreur lors du chargement')
  }
})

const loadServiceData = async (id) => {
  try {
    const response = await api.services.getServiceById(id)
    const service = response.data

    Object.assign(form, {
      nom: service.nom || '',
      description: service.description || '',
      prix: service.prix || 0,
      duree: service.duree || 30,
      categorie: service.categorie?._id || service.categorie || '',
      disponible_en_ligne: service.disponibilite === 'en_ligne' || service.disponible_en_ligne || true
    })
  } catch (error) {
    console.error('Erreur lors du chargement du service:', error)
    toast.error('Erreur lors du chargement du service')
  }
}

const handleSubmit = async () => {
  if (!form.nom || !form.prix || !form.duree || !form.categorie) {
    toast.error('Veuillez remplir tous les champs obligatoires', { position: 'top-right' })
    return
  }

  try {
    isSubmitting.value = true

    const serviceData = {
      nom: form.nom,
      description: form.description,
      prix: form.prix,
      duree: form.duree,
      categorie: form.categorie,
      disponibilite: form.disponible_en_ligne ? 'en_ligne' : 'salon',
      statut: 'actif'
    }

    if (isEditMode.value) {
      await api.services.updateService(editId.value, serviceData)
      toast.success('Service modifié avec succès !', { position: 'top-right' })
    } else {
      await api.services.createService(serviceData)
      toast.success('Service créé avec succès !', { position: 'top-right' })
    }

    setTimeout(() => router.push('/services'), 500)
  } catch (error) {
    console.error(error)
    toast.error(`Erreur lors de ${isEditMode.value ? 'la modification' : 'la création'}`, { position: 'top-right' })
  } finally {
    isSubmitting.value = false
  }
}
</script>
